import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { Truck, Package, CaretDown } from "@phosphor-icons/react";

/**
 * Small badge on the Show-Up Card: matches this WO's AI materials list against
 * the current user's Truck Stock. Silent if the WO has no AI materials list
 * (so we don't pollute jobs where the AI hasn't parsed materials).
 */
export default function TruckStockChip({ jobId }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!jobId) return;
    api.get(`/truck-stock/wo-match/${jobId}`)
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, [jobId]);

  if (!data || !data.has_materials) return null;

  const need = data.need || [];
  const have = data.have || [];
  const pct = data.match_pct ?? 0;
  const color = pct >= 80 ? "bg-emerald-500" : pct >= 40 ? "bg-amber-500" : "bg-red-600";

  return (
    <div data-testid="truck-stock-chip" className="mt-2">
      <button onClick={() => setOpen(v => !v)}
        className={`inline-flex items-center gap-1 px-2 py-1 text-white font-black uppercase text-[10px] tracking-widest ${color} border-2 border-[#0f172a]`}>
        <Truck weight="fill" size={12}/>
        {have.length}/{have.length + need.length} PARTS ON TRUCK · {pct}%
        <CaretDown weight="bold" size={12} className={`transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && (
        <div className="mt-2 p-2 border-2 border-slate-900 bg-white text-xs">
          {have.length > 0 && (
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700 mb-1">✓ On the truck</div>
              <ul className="space-y-0.5 mb-2">
                {have.map((h, i) => (
                  <li key={i} className="text-slate-700">
                    <span className="font-mono text-emerald-700 mr-1">×{h.qty_have}</span> {h.item}
                    <span className="text-slate-400 text-[10px]"> · need {h.qty_needed}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {need.length > 0 && (
            <div>
              <div className="text-[9px] font-black uppercase tracking-widest text-red-700 mb-1">🏬 Grab before rolling</div>
              <ul className="space-y-0.5">
                {need.map((n, i) => (
                  <li key={i} className="text-slate-800">
                    <Package weight="bold" size={10} className="inline mr-1 text-red-600"/>
                    <span className="font-black">{n.item}</span>
                    <span className="text-slate-400 text-[10px]"> · qty {n.qty_needed}</span>
                    {n.notes && <div className="pl-4 text-[10px] italic text-slate-500">{n.notes}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <Link to="/truck-stock" className="mt-2 inline-block text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:text-cyan-900">
            Update my truck stock →
          </Link>
        </div>
      )}
    </div>
  );
}
